
import { defineComponent, PropType } from 'vue'
import SocialBannerNew from '@/components/library/AsahiSocialBanner.vue'
import AsahiLogoNew from '@/components/library/assets/AsahiLogoNew.vue'
import { Collapse } from 'bootstrap'
import { mapGetters, mapState } from 'vuex'
import arrowdownload from '@/assets/icons/library/arrowdownloadwhite.svg'
import refreshAfterChangeLanguage from '@/mixins/refreshAfterChangeLanguage'

import { useRoute } from 'vue-router'
import { CommonMutationType } from '@/store/modules/common/mutation-type'
import { constants } from '@/constants'

interface FooterLink {
  label: string
  href?: string
  link?: string
  target?: string
  name?: string
}

interface RoleDetail {
  rolename: string;
  roleid: string;
  permission: string;
  roleType: string;
  customerType: string;
  possibleRoles: string;
}

interface FooterSection {
  name?: string
  title?: string
  label?: string
  links?: FooterLink[]
  children?: FooterLink[]
}

interface HeaderData {
  label: string;
  children: any[];
  name: string;
}

interface Item {
  label?: string;
  link?: string;
  target?: string;
  order?: string;
}

export default defineComponent({
  name: 'AsahiBsFooter',
  components: { AsahiLogoNew, SocialBannerNew },
  emits: ['languagechange', 'logout'],
  props: {
    headerData: {
      type: Object as PropType<HeaderData[]>,
      required: true
    },
    language: {
      type: Array as () => string[],
      required: true
    },
    activeLanguage: {
      type: String,
      required: true
    },
    footerData: {
      type: Object as () => FooterSection[],
      required: true
    },
    brandData: {
      type: Object as () => FooterLink[],
      required: true
    },
    cookies: {
      type: Object as () => FooterLink[],
      required: true
    },
    termsOfUse: {
      type: Array as () => FooterLink[],
      required: true
    },
    privacyPolicy: {
      type: Array as () => FooterLink[],
      required: true
    }
  },
  mixins: [refreshAfterChangeLanguage],
  data: () => ({
    openLanguage: false,
    combinedFooterData: [] as FooterSection[],
    columns: [] as Item[][],
    arrowdownload,
    route: useRoute(),
    isRop: false
  }),
  computed: {
    ...mapState('main', ['pageNotFound']),
    ...mapGetters('main', ['getPermissions']),
    ...mapState('basket', ['id', 'query', 'items']),

    isBasicCustomer () {
      const customerTypeArray = this.$store.state.auth.user?.permissionSchema?.filter(permission => permission?.rpDetails.some((item: RoleDetail) => item?.customerType?.toLowerCase() !== ''))
      const customerType = customerTypeArray && customerTypeArray[0] && customerTypeArray[0]?.rpDetails[0] ? customerTypeArray[0]?.rpDetails[0]?.rolename : ''
      return (customerType.toLowerCase() === 'ontrade basic') || (customerType.toLowerCase() === 'distributor basic')
    },

    user () {
      return this.$store.state.auth.user
    },
    isOntradeCustomer () {
      return this.user?.permissionSchema?.[0]?.rpDetails[0]?.customerType === 'Ontrade'
    },
    isStandardOntrade ():boolean {
      return this.$store.state.auth.user?.permissionSchema?.[0]?.rpDetails[0]?.rolename === 'Ontrade Standard'
    },

    isDistributorStandard ():boolean {
      return this.$store.state.auth.user?.permissionSchema?.[0]?.rpDetails[0]?.rolename === 'Distributor Standard'
    },

    isLoggedIn () {
      return this.user.isLoggedIn // && !!this.user.userValidation
    },
    freeBeersInfo () {
      return this.$store.state.catalog.productFreeBeersInfo.getFreeBeerCount[0]
    },
    freeBeersRunning () {
      return this.$store.state.catalog.freeBeersRunning
    },
    currentPage () {
      return this.$route.path
    },
    cartId () {
      return this.$store.state?.basket?.id
    }

  },
  methods: {

    isNewPage (link: { label: string; href?: string | undefined; link?: string | undefined; target?: string | undefined; name?: string | undefined }) {
      const newPages = ['pubMenu', 'eLearning', 'Beercoin']

      return link.name && newPages.findIndex(e => e === link.name) !== -1
    },

    async checkItemsData () {
      if (this.$route.path !== '/catalog/quickorder' && this.freeBeersInfo.enabled === true &&
      Number(this.freeBeersInfo.count) > 0) {
        const freeBeerItems = this.items ? this.items.filter((item: any) => item.is_free_beer === 1) : []
        if (freeBeerItems && freeBeerItems.length === 0) {
          if (!this.id || this.id === '') {
            await this.$store.dispatch('basket/FETCH_BASKET_ID', {}, { root: true })
          }
          if (this.$route.path !== '/catalog/quickorder' && freeBeerItems &&
          freeBeerItems.length === 0) {
            await this.$store.dispatch(
              'catalog/FETCH_FREEBEER_PRODUCT',
              {
                cartId: this.id

              },
              { root: true }
            )
          }
          if (this.id) {
            await this.$store.dispatch('basket/FETCH_CART_QUERY', { cart_id: this.id }, { root: true })
          }
        }
      }
    },

    closeLangAccordion () {
      const myCollapse = document.getElementById('asahi-bs-footer-accordion-collapse-language')
      if (myCollapse?.classList.contains('show')) {
        const bsCollapse = new Collapse(myCollapse, {
          toggle: true
        })
      }
    },
    chunkArray (array: Item[], size: number): Item[][] {
      return Array.from({ length: Math.ceil(array.length / size) }, (_, index) =>
        array.slice(index * size, index * size + size)
      )
    },
    getLinkListBeforeLogin () {
      this.columns = this.chunkArray(this.combinedFooterData.slice(1), 10)
    },

    openLanguageDropdown () {
      this.openLanguage = !this.openLanguage
    },
    setMagentoStore (language: string) {
      const storeLanguage = language === 'en' ? 'en' : 'pl'
      localStorage.setItem('store', `LivePortal_PL_${storeLanguage}`)
    },
    changeLanguage (lang: string) {
      if (lang !== this.activeLanguage) {
        this.$emit('languagechange', lang)
        this.setMagentoStore(lang)
      }
    },
    getFullLanguageName (code: string): string {
      const languageMap: { [key: string]: string } = { en: 'English', pl: 'Polski' }
      return languageMap[code] || code
    },

    combineFooterData () {
      const orderedData: FooterSection[] = []
      const ordersSection = this.footerData.find(section => section.name === 'Orders')
      const otherSections = this.footerData.filter(section => section.name !== 'Orders' && section.name !== 'Create an order')
      const myAccountSection = this.newMenu().find(section => section.name === 'profile')
      const otherSection = this.newMenu().find(s => s.name === 'other')

      if (this.isLoggedIn && ordersSection) {
        orderedData.push(ordersSection)
      }

      if (this.brandData) {
        orderedData.push({
          title: this.$t('Our Brands'),
          links: this.brandData
        })
      }

      if (this.isLoggedIn) {
        otherSections.forEach((section, index) => {
          if (index === 1) {
            if (myAccountSection) {
              orderedData.push(myAccountSection)
            }
          }
          orderedData.push(section)
        })

        if (myAccountSection && !orderedData.includes(myAccountSection) && orderedData.length < 4) {
          orderedData.push(myAccountSection)
        }

        if (otherSection) {
          orderedData.push(otherSection)
        }
      } else {
        const supportSec: FooterSection =
          {
            label: this.$t('Support'),
            name: 'Support',
            children: [
              {
                label: this.$t('Contacts'),
                href: '/contact'
              }
            ]
          }

        orderedData.push(supportSec)
      }

      this.combinedFooterData = orderedData
    },
    menuNavigate (link: FooterLink) {
      console.log('link?.name', link?.name)
      console.log('link.href', link.href)
      if (link?.name === 'Szkolenia 2') {
        window.gigya.fidm.saml.initSSO({
          spName: this.getSPName(link?.name),
          redirectURL: link.href
        })
      } else if (link?.name === 'Beercoin 10') {
        window.open(constants.gigyaConfig.ssoRedirectURL)
      } else {
        const regex = /^(http|https)/
        if (link?.link?.match(regex)) {
          window.location.href = link.link
        } else {
          this.$router.push(link)
        }
      }
    },
    getSPName (name: string) {
      let spName = ''
      if (name === 'Szkolenia 2') {
        spName = 'eLearning'
      }
      if (name === 'Beercoin 10') {
        spName = 'beercoin'
      }
      console.log('spName', spName)
      return spName
    },
    newMenu (): FooterSection[] {
      // Define the standard order of menu items

      const menuItems = [
        {
          label: this.$t('My Profile'),
          href: '/my-profile/details'
        },
        {
          label: this.$t('Notifications'),
          href: '/notifications'
        }
      ]

      if (!this.isBasicCustomer) {
        menuItems.push({
          label: this.$t('My Documents'),
          href: '/my-documents'
        },
        {
          label: this.$t('Contracts'),
          href: '/contracts'
        })
      }

      // Filter menu items based on user type
      const profileChildren = menuItems.filter(item => {
        if (this.isOntradeCustomer) {
          return item.href === '/my-profile/details' || item.href === '/notifications' || (!this.isStandardOntrade && item.href === '/contracts')
        } else if (this.isDistributorStandard) {
          return item.href !== '/contracts' // Exclude contracts for distributor standard
        }
        return true // Include all for other users
      })

      // Final menu structure
      const returnArr : FooterSection[] = [
        {
          label: this.$t('My Profile'),
          name: 'profile',
          children: profileChildren
        }

      ]

      if (!this.isOntradeCustomer) {
        returnArr.push({
          label: this.$t('Other links'),
          name: 'other',
          links: [
            {
              label: this.$t('E-invoices'),
              href: 'https://edi.edoc-online.com/edi-archive/'
            }
          ]
        })
      }

      return returnArr
    },

    async fetchFreeBeerInfo () {
      this.$store.commit(CommonMutationType.INCREMENT_LOADING)
      await this.$store.dispatch(
        'catalog/FETCH_FREEBEERS_INFO',
        { root: true }
      )
      this.$store.commit(CommonMutationType.DECREMENT_LOADING)
    },
    refresh () {
      if (this.footerData) {
        this.combineFooterData()
      }
    }
  },
  mounted () {
    this.refresh()
    this.fetchFreeBeerInfo()
  },
  watch: {
    footerData: {
      deep: true,
      handler (newVal) {
        if (newVal && newVal.length > 0) {
          this.combineFooterData()
        }
      }
    },
    brandData: {
      deep: true,
      handler (newVal) {
        if (newVal && Object.keys(newVal).length > 0) {
          this.combineFooterData()
        }
      }
    },
    items (newValue, oldValue) {
      if (
        newValue && newValue.length !== oldValue.length &&
        this.freeBeersRunning === false && this.currentPage !== '/cart'
      ) {
        this.freeBeersRunning = true
        this.checkItemsData()
        this.freeBeersRunning = false
      } else if (this.currentPage === '/cart' && newValue.length === 0 && this.route.query.rop !== 'true') {
        this.$navigate('/catalog/products')
      }
    },
    currentPage (newValue, oldValue) {
      if (newValue && newValue !== oldValue && (oldValue === '/catalog/quickorder' || oldValue !== '/login') && newValue !== '/cart' && newValue !== '/thanks') {
        this.checkItemsData()
      }
    },
    cartId (newValue, oldValue) {
      if (newValue && newValue !== oldValue && oldValue !== '') {
        this.checkItemsData()
      }
    }
  }
})
